import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaBell, FaClock, FaFileAlt, FaPaperPlane, FaPen, FaPlusCircle, FaSearch } from "react-icons/fa";
import { toast } from "react-toastify";
import api from "../api/httpClient";
import { API_PATHS } from "../api/apiPaths";
import { getCachedOrFetch, getTenantCacheKey, invalidateCache } from "../utils/requestCache";
import { useAppTheme } from "../context/AppThemeContext";
import AdminResponsiveTabs from "./AdminResponsiveTabs";
import AdminModalShell from "./AdminModalShell";
import AdminMobileViewToggle from "./AdminMobileViewToggle";
import ModuleImportButton from "./ModuleImportButton";
import ModuleRefreshButton from "./ModuleRefreshButton";

const emptyForm = {
  id: "",
  title: "",
  audience: "Students",
  channel: "In-app",
  priority: "MEDIUM",
  status: "DRAFT",
  scheduledFor: "",
  message: "",
};

const normalizeNotification = (record = {}, index = 0) => ({
  id: record.id || record.notificationId || record._id || `notice-${index + 1}`,
  title: record.title || record.subject || `Notice ${index + 1}`,
  audience: record.audience || record.targetAudience || "Students",
  channel: record.channel || record.deliveryChannel || "In-app",
  priority: String(record.priority || "MEDIUM").toUpperCase(),
  status: String(record.status || "DRAFT").toUpperCase(),
  scheduledFor: record.scheduledFor || record.publishAt || "",
  message: record.message || record.body || "",
});

const extractNotifications = (responseData) => {
  const payload = responseData?.data ?? responseData ?? [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.notifications)) return payload.notifications;
  if (Array.isArray(payload?.content)) return payload.content;
  return [];
};

const badgeTone = (status) => {
  if (status === "PUBLISHED") return "bg-emerald-100 text-emerald-700";
  if (status === "SCHEDULED") return "bg-sky-100 text-sky-700";
  return "bg-slate-100 text-slate-700";
};

export default function AdminNotificationsManagement({ createMode = false }) {
  const navigate = useNavigate();
  const { isDarkMode } = useAppTheme();
  const [activeTab, setActiveTab] = useState("table");
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [selectedId, setSelectedId] = useState("");
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [mobileRecordView, setMobileRecordView] = useState("cards");
  const [form, setForm] = useState(emptyForm);
  const [refreshVersion, setRefreshVersion] = useState(0);

  useEffect(() => {
    const fetchNotifications = async () => {
      setLoading(true);
      try {
        const response = await getCachedOrFetch(getTenantCacheKey("admin:notifications"), () => api.get(API_PATHS.admin.notifications()), { forceRefresh: refreshVersion > 0 });
        const nextRecords = extractNotifications(response.data).map(normalizeNotification);
        if (!createMode && nextRecords.length > 0) {
          setNotifications(nextRecords);
          setSelectedId(nextRecords[0].id);
        } else if (nextRecords.length > 0) {
          setNotifications(nextRecords);
          setSelectedId("");
        } else {
          setNotifications([]);
          setSelectedId("");
        }
      } catch {
        setNotifications([]);
        setSelectedId("");
      } finally {
        setLoading(false);
      }
    };
    fetchNotifications();
  }, [refreshVersion, createMode]);

  useEffect(() => {
    if (createMode) {
      setSelectedId("");
      setForm(emptyForm);
      setIsFormOpen(true);
    }
  }, [createMode]);

  const selectedNotification = useMemo(() => (
    notifications.find((item) => item.id === selectedId) || null
  ), [notifications, selectedId]);

  useEffect(() => {
    if (createMode || !selectedId || !selectedNotification) {
      setForm(emptyForm);
      return;
    }
    setForm(selectedNotification);
  }, [selectedNotification, selectedId, createMode]);

  const filteredNotifications = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    return notifications.filter((item) => (
      (statusFilter === "ALL" || item.status === statusFilter)
      && (!query
      || item.title.toLowerCase().includes(query)
      || item.message.toLowerCase().includes(query)
      || item.audience.toLowerCase().includes(query)
      || item.channel.toLowerCase().includes(query)
      || item.status.toLowerCase().includes(query))
    ));
  }, [notifications, searchTerm, statusFilter]);

  const summary = useMemo(() => ({
    total: notifications.length,
    published: notifications.filter((item) => item.status === "PUBLISHED").length,
    scheduled: notifications.filter((item) => item.status === "SCHEDULED").length,
    draft: notifications.filter((item) => item.status === "DRAFT").length,
  }), [notifications]);

  const updateForm = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const resetForm = () => {
    setSelectedId("");
    setForm(emptyForm);
  };
  const openCreateForm = () => {
    setSelectedId("");
    setForm(emptyForm);
    setIsFormOpen(true);
    if (!createMode) {
      navigate("/admin/notifications/create");
    }
  };
  const openEditForm = (id) => {
    const target = notifications.find((item) => item.id === id);
    setSelectedId(id);
    if (target) {
      setForm(target);
    }
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setSelectedId("");
    setForm(emptyForm);
    setIsFormOpen(false);
    if (createMode) {
      navigate("/admin/notifications");
    }
  };

  const upsertNotification = (nextRecord) => {
    setNotifications((current) => {
      const exists = current.some((item) => item.id === nextRecord.id);
      if (!exists) return [nextRecord, ...current];
      return current.map((item) => (item.id === nextRecord.id ? nextRecord : item));
    });
    setSelectedId(nextRecord.id);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);

    const payload = {
      title: form.title.trim(),
      audience: form.audience,
      channel: form.channel,
      priority: form.priority,
      status: form.status,
      scheduledFor: form.scheduledFor,
      message: form.message.trim(),
    };
    const isEditing = Boolean(form.id);

    try {
      const response = isEditing
        ? await api.put(API_PATHS.admin.notificationById(form.id), payload)
        : await api.post(API_PATHS.admin.notifications(), payload);

      const nextRecord = normalizeNotification(
        { ...(response.data?.data ?? response.data ?? {}), ...payload, id: isEditing ? form.id : undefined },
        notifications.length
      );
      upsertNotification(nextRecord);
      invalidateCache(getTenantCacheKey("admin:notifications"));
      setSelectedId("");
      setForm(emptyForm);
      setIsFormOpen(false);
      if (createMode) navigate("/admin/notifications");
      toast.success(isEditing ? "Notification updated." : "Notification created.");
    } catch {
      toast.error("Unable to save notification. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`space-y-4 ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>
      <AdminResponsiveTabs tabs={[{ key: "table", label: "Notifications" }, { key: "summary", label: "Summary" }]} activeKey={activeTab} onChange={setActiveTab} tone="amber" isDarkMode={isDarkMode} />

      <div className={`border-x border-b p-6 shadow-lg ${isDarkMode ? "border-slate-800 bg-slate-900 shadow-black/20" : "border-slate-200 bg-white shadow-slate-200/60"}`}>
        {activeTab === "table" && (
          <>
            <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-sky-500">Communication center</p>
                <h2 className={`mt-1 text-2xl font-bold ${isDarkMode ? "text-white" : "text-slate-900"}`}>Notifications</h2>
                <p className="mt-1 text-sm text-slate-500">Create, schedule, and track school-wide communication.</p>
              </div>
              <div className="flex w-full gap-2 md:w-auto">
                <div className="relative w-full md:w-80">
                  <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    className="w-full rounded-xl border border-slate-300 py-2.5 pl-10 pr-3 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-100"
                    placeholder="Search title, audience, channel"
                  />
                </div>
                <ModuleRefreshButton onRefresh={() => setRefreshVersion((value) => value + 1)} />
                <ModuleImportButton moduleKey="notifications" label="Import notifications" />
                <button
                  type="button"
                  onClick={openCreateForm}
                  className="inline-flex items-center gap-2 rounded-xl bg-sky-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-sky-700"
                >
                  <FaPlusCircle />
                  Compose
                </button>
              </div>
            </div>

            <div className="mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              {[
                ["ALL", "All notifications", summary.total, FaBell, "text-slate-500"],
                ["PUBLISHED", "Published", summary.published, FaPaperPlane, "text-emerald-500"],
                ["SCHEDULED", "Scheduled", summary.scheduled, FaClock, "text-sky-500"],
                ["DRAFT", "Drafts", summary.draft, FaFileAlt, "text-amber-500"],
              ].map(([key, label, count, Icon, tone]) => (
                <button key={key} type="button" onClick={() => setStatusFilter(key)} className={`flex items-center gap-3 rounded-2xl border p-4 text-left transition ${statusFilter === key ? (isDarkMode ? "border-sky-700 bg-sky-950/30" : "border-sky-300 bg-sky-50") : (isDarkMode ? "border-slate-800 bg-slate-950 hover:border-slate-700" : "border-slate-200 bg-white hover:border-slate-300")}`}>
                  <span className={`grid h-10 w-10 place-items-center rounded-xl ${isDarkMode ? "bg-slate-900" : "bg-slate-100"} ${tone}`}><Icon /></span>
                  <span><span className="block text-xl font-black">{count}</span><span className="block text-xs text-slate-500">{label}</span></span>
                </button>
              ))}
            </div>

            <div className="mb-3">
              <AdminMobileViewToggle value={mobileRecordView} onChange={setMobileRecordView} isDarkMode={isDarkMode} />
            </div>

            {mobileRecordView === "cards" && (
              <div className="space-y-3 md:hidden">
                {loading ? (
                  <p className="rounded-2xl bg-slate-50 p-6 text-center text-sm text-slate-500">Loading notifications...</p>
                ) : filteredNotifications.length === 0 ? (
                  <p className="rounded-2xl bg-slate-50 p-6 text-center text-sm text-slate-500">No notifications found.</p>
                ) : filteredNotifications.map((item) => (
                  <article key={item.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="flex items-start justify-between gap-3"><div><h3 className="font-bold text-slate-900">{item.title}</h3><p className="mt-1 text-xs text-slate-500">{item.audience} · {item.channel}</p></div><span className={`rounded-full px-2 py-1 text-[10px] font-semibold ${badgeTone(item.status)}`}>{item.status}</span></div>
                    <div className="mt-3 rounded-xl bg-slate-50 p-3 text-xs text-slate-600"><p><span className="text-slate-400">Priority:</span> {item.priority}</p><p className="mt-1"><span className="text-slate-400">Schedule:</span> {item.scheduledFor || "Instant"}</p></div>
                    <button type="button" onClick={() => openEditForm(item.id)} className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-300 px-3 py-2 text-xs font-bold text-slate-700"><FaPen /> Update notification</button>
                  </article>
                ))}
              </div>
            )}

            <div className={`${mobileRecordView === "cards" ? "hidden md:block" : "block"} overflow-hidden rounded-2xl border border-slate-200`}>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-100 text-slate-600">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold">Title</th>
                      <th className="px-4 py-3 text-left font-semibold">Audience</th>
                      <th className="px-4 py-3 text-left font-semibold">Channel</th>
                      <th className="px-4 py-3 text-left font-semibold">Priority</th>
                      <th className="px-4 py-3 text-left font-semibold">Status</th>
                      <th className="px-4 py-3 text-left font-semibold">Schedule</th>
                      <th className="px-4 py-3 text-left font-semibold">Action</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white text-slate-800">
                    {loading ? (
                      <tr>
                        <td colSpan="7" className="px-4 py-8 text-center text-slate-500">Loading notifications...</td>
                      </tr>
                    ) : filteredNotifications.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="px-4 py-8 text-center text-slate-500">No notifications found.</td>
                      </tr>
                    ) : filteredNotifications.map((item) => (
                      <tr key={item.id} className="border-t border-slate-200 hover:bg-slate-50">
                        <td className="px-4 py-4"><div className="flex min-w-[18rem] items-start gap-3"><span className={`mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-xl ${item.status === "PUBLISHED" ? "bg-emerald-100 text-emerald-600" : item.status === "SCHEDULED" ? "bg-sky-100 text-sky-600" : "bg-slate-100 text-slate-500"}`}><FaBell /></span><div><p className="font-bold">{item.title}</p><p className="mt-1 line-clamp-2 max-w-md text-xs leading-5 text-slate-500">{item.message || "No message preview"}</p></div></div></td>
                        <td className="px-4 py-3"><span className="rounded-full bg-violet-50 px-2.5 py-1 text-xs font-semibold text-violet-700">{item.audience}</span></td>
                        <td className="px-4 py-3"><span className="rounded-full bg-sky-50 px-2.5 py-1 text-xs font-semibold text-sky-700">{item.channel}</span></td>
                        <td className="px-4 py-3"><span className={`text-xs font-bold ${item.priority === "HIGH" ? "text-rose-600" : item.priority === "LOW" ? "text-slate-500" : "text-amber-600"}`}>{item.priority}</span></td>
                        <td className="px-4 py-3">
                          <span className={`rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] ${badgeTone(item.status)}`}>
                            {item.status}
                          </span>
                        </td>
                        <td className="px-4 py-3">{item.scheduledFor || "Instant"}</td>
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={() => openEditForm(item.id)}
                            className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                          >
                            <FaPen />
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {activeTab === "summary" && (
          <div className="space-y-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Summary</p>
              <h2 className="mt-1 text-2xl font-bold text-slate-900">Notification metrics</h2>
            </div>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              {[
                { label: "Total", value: summary.total, icon: FaBell },
                { label: "Published", value: summary.published, icon: FaBell },
                { label: "Scheduled", value: summary.scheduled, icon: FaBell },
                { label: "Draft", value: summary.draft, icon: FaBell },
              ].map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.label} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">{item.label}</p>
                      <Icon className="text-slate-400" />
                    </div>
                    <p className="mt-2 text-2xl font-bold text-slate-900">{item.value}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <AdminModalShell isOpen={isFormOpen || createMode} onClose={closeForm} isDarkMode={isDarkMode} maxWidth="max-w-3xl" pageMode={createMode}>
        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-lg">
          <h3 className="text-xl font-bold text-slate-900">{form.id ? "Update Notification" : "Create Notification"}</h3>
          <form onSubmit={handleSubmit} className="mt-5 space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Title</label>
              <input
                type="text"
                value={form.title}
                onChange={(event) => updateForm("title", event.target.value)}
                className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-100"
                required
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Audience</label>
                <select value={form.audience} onChange={(event) => updateForm("audience", event.target.value)} className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm">
                  <option value="Students">Students</option>
                  <option value="Parents">Parents</option>
                  <option value="Teachers">Teachers</option>
                  <option value="All Users">All users</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Channel</label>
                <select value={form.channel} onChange={(event) => updateForm("channel", event.target.value)} className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm">
                  <option value="In-app">In-app</option>
                  <option value="Email">Email</option>
                  <option value="SMS">SMS</option>
                  <option value="Push">Push</option>
                </select>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Priority</label>
                <select value={form.priority} onChange={(event) => updateForm("priority", event.target.value)} className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm">
                  <option value="LOW">Low</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="HIGH">High</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Status</label>
                <select value={form.status} onChange={(event) => updateForm("status", event.target.value)} className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm">
                  <option value="DRAFT">Draft</option>
                  <option value="SCHEDULED">Scheduled</option>
                  <option value="PUBLISHED">Published</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Publish time</label>
                <input
                  type="text"
                  value={form.scheduledFor}
                  onChange={(event) => updateForm("scheduledFor", event.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm"
                  placeholder="2026-04-15 09:00"
                />
              </div>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Message</label>
              <textarea
                value={form.message}
                onChange={(event) => updateForm("message", event.target.value)}
                rows="5"
                className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm"
                required
              />
            </div>
            <div className="flex flex-wrap gap-3">
              <button type="submit" disabled={saving} className="rounded-xl bg-amber-500 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-600 disabled:opacity-70">
                {saving ? "Saving..." : form.id ? "Update" : "Create"}
              </button>
              <button type="button" onClick={resetForm} className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100">
                Reset
              </button>
            </div>
          </form>
        </div>
      </AdminModalShell>
    </div>
  );
}
